import { useEffect, useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import {
  setTokenGetter,
  setUnauthorizedRedirectHandler,
  setNotFoundRedirectHandler,
} from '@/configs/axios.config'

/**
 * Hook to setup the HTTP client with Clerk authentication and navigation handlers.
 * Should be called once at the app level (e.g., in _authenticated layout).
 *
 * @returns {boolean} isReady - Whether the HTTP client is fully configured
 */
export function useHttpClientSetup(): boolean {
  const { getToken, signOut, isLoaded } = useAuth()
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    // Don't set up until Clerk auth is fully loaded
    if (!isLoaded) {
      return
    }

    // Set up the token getter for axios
    // This uses Clerk's getToken which handles token refresh automatically
    setTokenGetter(async () => {
      try {
        const token = await getToken()
        return token
      } catch (error) {
        console.error('Failed to get Clerk token:', error)
        return null
      }
    })

    // Set up unauthorized redirect handler
    setUnauthorizedRedirectHandler(() => {
      signOut()
        .catch((error) => {
          console.error('Failed to sign out:', error)
        })
        .finally(() => {
          window.location.href = '/'
        })
    })

    // Set up not found redirect handler
    setNotFoundRedirectHandler(() => {
      window.location.href = '/not-found'
    })

    // Defer state update to avoid synchronous setState in effect
    queueMicrotask(() => {
      setIsReady(true)
    })

    // Cleanup on unmount
    return () => {
      setTokenGetter(null)
      setUnauthorizedRedirectHandler(null)
      setNotFoundRedirectHandler(null)
      setIsReady(false)
    }
  }, [getToken, signOut, isLoaded])

  return isReady
}
